export class Tag {
  id: number
  key: string
  type: string
  weight: number

  constructor (attributes?) {
    Object.assign(this, attributes)
  }
}

export enum TrackingLevel {
  NONE = 'none',
  SKELETON = 'skeleton'
}

export class ExerciseResponse {

  id: number
  name: string
  number: number

  videos: {
    url: string
    content_type: string
  }[]

  exerciseTags: Tag[] = []

  equipment: string[] = []
  defaultWeightFemale: number
  defaultWeightMale: number
  hasTempo: boolean
  hasSides: boolean
  durationMedium: number
  complexityLevel: number
  subsequentRestPeriod: number

  primaryMuscles: string[]
  secondaryMuscles: string[]

  state: string
  custom: boolean

  trackingLevel: TrackingLevel
  trackingLevel2d: TrackingLevel

  side: string

  videoReps: number
  isFavorite?: boolean | null
  private translatedName: string = ''

  constructor (attributes?) {
    Object.assign(this, attributes)
    this.equipment = this.equipment.filter(equipment => equipment && equipment !== '')
    this.exerciseTags.forEach(tag => { tag.key = tag.key.replace('/', '_').toLowerCase() })
    // Handle snake_case from backend
    if (attributes && 'is_favorite' in attributes) {
      this.isFavorite = attributes.is_favorite
    }
  }

  /**
   * Retrieve a list containing all tags; to be used for exercise filtering.
   *
   * @returns {string[]}
   */
  getTagKeys (): string[] {
    let tags: string[] = []
    this.exerciseTags.forEach(tag => {
      tags.push(tag.key.replace('/', '_').toLowerCase())
    })

    return tags
  }
  getTagKeysWOHealthLimitations (): string[] {
    let tags: string[] = []
    this.exerciseTags.forEach(tag => {
      if (tag.type === 'ExerciseTags::HealthLimitation') {
        return
      }
      tags.push(tag.key.replace('/', '_').toLowerCase())
    })

    return tags
  }
  /**
   * Build the health limitations lists.
   */
  getHealthLimitations (allLimitations: string[]) {
    let notAllowed: string[] = []
    let allowed: string[] = [...allLimitations]
    this.exerciseTags.forEach(tag => {
      if (tag.type === 'ExerciseTags::HealthLimitation') {
        notAllowed.push(tag.key)
        allowed.splice(allowed.indexOf(tag.key), 1)
      }
    })

    return { allowed, notAllowed }
  }

  /**
   * Get the Gif representation of the exercise.
   *
   * @returns {string}
   */
  getGifURL (): string {
    let URL = ''
    this.videos.forEach(video => {
      if (video.content_type === 'image/gif') {
        URL = video.url
      }
    })

    return URL
  }

  /**
   * Get the video urls of the exercise.
   *
   * @returns {string}
   */
  getVideoURLs (): { webm, mp4, gif } {
    let URLs = { webm: '', mp4: '', gif: '' }
    this.videos.forEach(video => {
      if (video.content_type === 'video/webm') {
        URLs.webm = video.url
      }
      if (video.content_type === 'video/mp4') {
        URLs.mp4 = video.url
      }
      if (video.content_type === 'image/gif') {
        URLs.gif = video.url
      }
    })

    return URLs
  }

  /**
   * Get the default weight for female first, and if it does not exits, use the value for male.
   *
   * @returns {number}
   */
  getDefaultWeight (): number {
    return this.defaultWeightFemale || this.defaultWeightMale
  }

  /** Get the weights equipment code used for the exercise. Only some of the exercises have weights.
   *
   * @returns {string}
   */
  getWeightEquipment (): string {
    let equipment = ''
    this.equipment.forEach(item => {
      if (item.match(/^(barbell|dumbbell|dumbbells|medicine_ball|kettlebells)$/) !== null) {
        equipment = item
      }
    })
    return equipment
  }

  /**
   * The name of an exercise can be translated (based on number), or a custom name.
   */
  getName () {
    return this.translatedName || this.name
  }

  setTranslatedName (translatedName) {
    this.translatedName = translatedName
  }

  addExerciseTag (tag: Tag) {
    this.exerciseTags.push(tag)
  }

  is2DTracked (): boolean {
    return this.trackingLevel2d === TrackingLevel.SKELETON
  }

  is3DTracked (): boolean {
    return this.trackingLevel === TrackingLevel.SKELETON
  }
}
